import React, { useState, useMemo, useCallback } from 'react';
import { Box, Chip, Grid, Paper, Typography, alpha, useTheme } from '@mui/material';
import { CalendarToday, Person } from '@mui/icons-material';
import { useSelector, shallowEqual } from 'react-redux';
import { RootStateType } from '../../store';
import moment from 'moment';

import DownloadShipmentLabelAndInvoice from '@Components/DownloadShipmentLabelAndInvoice';
import ShipmentKycUpload from '@Components/ShipmentKycUpload';
import { CSP_TYPES, hideDownloadCarrierAWBAction } from '@Constants/Shipment';

export function ShipmentHistoryActions() {
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const theme = useTheme();

  const { shipmentData, adminShipmentData } = useSelector(
    (state: RootStateType) => ({
      shipmentData: state.shipment.shipmentById,
      adminShipmentData: state.adminShipment.shipmentById,
    }),
    shallowEqual,
  );

  const shipment = shipmentData || adminShipmentData;
  const shipmentNumber = shipment?.shipmentNumber || '';

  const formattedDate = useMemo(
    () => (shipment?.createdAt ? moment(shipment.createdAt).format('DD MMM YYYY, hh:mm a') : '—'),
    [shipment?.createdAt],
  );

  const placedBy = shipment?._placedById;
  const vendorShipmentModel = shipment?.vendorShipmentModel;

  const allowedCarrierCodes = useMemo(
    () =>
      new Set([
        CSP_TYPES.fedex.value,
        CSP_TYPES.ithinklogistics.value,
        CSP_TYPES.ithink_logistics_domestic.value,
        CSP_TYPES.linex.value,
        CSP_TYPES.delhivery.value,
        CSP_TYPES.trackon.value,
        CSP_TYPES.movin_ups.value,
        CSP_TYPES.dpduk.value,
        CSP_TYPES.shree_nandan_courier_limited.value,
        CSP_TYPES.dhlIndia.value,
        'cirro',
        CSP_TYPES.stage.value,
        CSP_TYPES.bluedart.value,
        CSP_TYPES.universal.value,
        CSP_TYPES.aramex.value,
        CSP_TYPES.dhl.value,
      ]),
    [],
  );

  const carrierAWBIcon = useMemo(() => {
    const code = shipment?.buyingPricing?.code;
    if (vendorShipmentModel && code && allowedCarrierCodes.has(code)) {
      return shipment?.buyingPricing?.serviceImage;
    }
    return '';
  }, [shipment, vendorShipmentModel, allowedCarrierCodes]);

  const hideCarrierAWB = useMemo(() => hideDownloadCarrierAWBAction(shipment), [shipment]);

  const handleOpen = useCallback(() => {
    setDocumentUploadModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDocumentUploadModalOpen(false);
  }, []);

  if (!shipment) return <></>;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: '14px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.06)',
          borderColor: alpha(theme.palette.primary.main, 0.25),
        },
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {/* Shipment Number & Status */}
        <Grid item xs={12} sm={6} md={3} lg={2.5}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.5px',
                color: 'primary.main',
                lineHeight: 1.3,
                mb: 0.5,
              }}
            >
              {shipmentNumber || '—'}
            </Typography>
            {shipment?.status && (
              <Chip
                label={shipment.status.toUpperCase()}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: 'primary.main',
                  height: 24,
                  borderRadius: '6px',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '& .MuiChip-label': { px: 1.2 },
                }}
              />
            )}
          </Box>
        </Grid>

        {/* Placed by */}
        <Grid item xs={12} sm={6} md={4} lg={3.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <Person sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500, display: 'block' }}>
                Placed by
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>
                {placedBy?.name || '—'}
                {(placedBy?.email || placedBy?.username) && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {placedBy?.username || placedBy?.email}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Created At */}
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <CalendarToday sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500, display: 'block' }}>
                Created at
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>{formattedDate}</Typography>
            </Box>
          </Box>
        </Grid>

        {/* Download Buttons */}
        <Grid item xs={12} sm={6} md={2} lg={3}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <DownloadShipmentLabelAndInvoice
              handleView={handleOpen}
              shipmentNumber={shipmentNumber}
              hideCarrierAWB={hideCarrierAWB}
              carrierAWBIcon={carrierAWBIcon}
            />
          </Box>
        </Grid>
      </Grid>

      {documentUploadModalOpen && (
        <ShipmentKycUpload shipmentNumber={shipmentNumber} open={documentUploadModalOpen} handleClose={handleClose} />
      )}
    </Paper>
  );
}

export default ShipmentHistoryActions;
